/* ==========================================================================
   Aura — HealthCrystal theme. Vanilla JS, geen dependencies.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  /* ---------- Helpers ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const formatMoney = (cents, format = theme.moneyFormat || '€{{amount_with_comma_separator}}') => {
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands, decimal) => {
      const fixed = n.toFixed(decimals).split('.');
      const whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed[1] ? `${whole}${decimal}${fixed[1]}` : whole;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return fmt(value, 2, ',', '.');
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_comma_separator':
        default: return fmt(value, 2, '.', ',');
      }
    });
  };

  const toast = (message) => {
    const el = $('.toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Drawers (menu, zoeken, winkelwagen) ---------- */
  let lastFocus = null;

  const openDrawer = (drawer, trigger) => {
    if (!drawer) return;
    lastFocus = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-overlay');
    $$('[aria-controls="' + drawer.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const focusTarget = $('[data-predictive-input]', drawer) || $('[role="dialog"]', drawer);
    if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 60);
  };

  const closeDrawer = (drawer) => {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    $$('[aria-controls="' + drawer.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (!$('[data-drawer].is-open')) document.body.classList.remove('has-overlay');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };

  document.addEventListener('click', (e) => {
    const close = e.target.closest('[data-drawer-close]');
    if (close) {
      closeDrawer(close.closest('[data-drawer]'));
      return;
    }
    const menuBtn = e.target.closest('[data-menu-open]');
    if (menuBtn) { openDrawer($('#MenuDrawer'), menuBtn); return; }

    const searchBtn = e.target.closest('[data-search-open]');
    if (searchBtn) { openDrawer($('#SearchModal'), searchBtn); return; }

    const cartBtn = e.target.closest('[data-cart-open]');
    if (cartBtn && $('#CartDrawer')) {
      e.preventDefault();
      openDrawer($('#CartDrawer'), cartBtn);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') $$('[data-drawer].is-open').forEach(closeDrawer);
  });

  /* ---------- Cart ---------- */
  const Cart = {
    sections() {
      return $('#CartDrawer') ? ['cart-drawer'] : [];
    },

    async add(body) {
      const sections = this.sections();
      const isForm = body instanceof FormData;
      if (sections.length) {
        if (isForm) {
          body.append('sections', sections.join(','));
          body.append('sections_url', window.location.pathname);
        } else {
          body.sections = sections.join(',');
          body.sections_url = window.location.pathname;
        }
      }
      const res = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: isForm ? body : JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      this.afterChange(data, true);
      return data;
    },

    async change(line, quantity) {
      const sections = this.sections();
      const res = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: sections.join(','), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.cartError);
      this.afterChange(data, false);
      if ($('[data-cart-page]')) await this.refreshCartPage();
      return data;
    },

    async updateNote(note) {
      await fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    },

    afterChange(data, opened) {
      if (theme.cartType === 'page' && opened && !$('#CartDrawer')) {
        window.location.href = routes.cart;
        return;
      }
      if (data.sections && data.sections['cart-drawer']) {
        this.renderDrawer(data.sections['cart-drawer']);
      } else {
        this.refreshCount();
      }
      if (opened) openDrawer($('#CartDrawer'));
    },

    renderDrawer(html) {
      const doc = parseHTML(html);
      const fresh = $('[data-cart-drawer-inner]', doc);
      const current = $('#CartDrawer [data-cart-drawer-inner]');
      if (fresh && current) {
        current.replaceWith(fresh);
        this.setCount(Number(fresh.dataset.cartCountValue || 0));
      }
    },

    async refreshCount() {
      const res = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      this.setCount(cart.item_count);
    },

    async refreshCartPage() {
      const res = await fetch(routes.cart);
      const doc = parseHTML(await res.text());
      const fresh = $('[data-cart-page]', doc);
      const current = $('[data-cart-page]');
      if (fresh && current) current.replaceWith(fresh);
    },

    setCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
    }
  };
  window.HCCart = Cart;

  // Product toevoegen (productpagina, kaarten, upsell)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const button = $('[type="submit"]', form);
    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    try {
      await Cart.add(new FormData(form));
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } else {
        toast(err.message);
      }
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-busy');
    }
  });

  // Aantallen in winkelwagen
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const item = btn.closest('.cart-item');
    item?.classList.add('is-loading');
    try {
      await Cart.change(Number(btn.dataset.line), Number(btn.dataset.qtyChange));
    } catch (err) {
      toast(err.message);
      item?.classList.remove('is-loading');
    }
  });

  document.addEventListener('change', async (e) => {
    const input = e.target.closest('.cart-item .qty__input');
    if (!input) return;
    const item = input.closest('.cart-item');
    item?.classList.add('is-loading');
    try {
      await Cart.change(Number(input.dataset.line), Math.max(0, parseInt(input.value, 10) || 0));
    } catch (err) {
      toast(err.message);
      item?.classList.remove('is-loading');
    }
  });

  const saveNote = debounce((value) => Cart.updateNote(value), 600);
  document.addEventListener('input', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (note) saveNote(note.value);
  });

  /* ---------- Custom elements ---------- */
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  // Aankondigingsbalk: wisselende berichten op mobiel
  define('announcement-bar', class extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      const interval = Number(this.dataset.interval) || 4000;
      this.timer = setInterval(() => {
        if (window.matchMedia('(min-width: 990px)').matches) return;
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, interval);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  });

  // Header: schaduw bij scrollen
  define('sticky-header', class extends HTMLElement {
    connectedCallback() {
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      // Desktop dropdowns openen bij hover
      $$('[data-hover-details]', this).forEach((d) => {
        d.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover)').matches) d.open = true; });
        d.addEventListener('mouseleave', () => { if (window.matchMedia('(hover: hover)').matches) d.open = false; });
      });
    }
  });

  define('menu-drawer', class extends HTMLElement {});
  define('cart-drawer', class extends HTMLElement {});

  // Zoeken met suggesties
  define('search-modal', class extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-predictive-input]', this);
      this.results = $('[data-predictive-results]', this);
      if (!this.input || !routes.predictiveSearch) return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.innerHTML = ''; return; }
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,page&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        if (!res.ok) return;
        const doc = parseHTML(await res.text());
        const content = $('.predictive', doc);
        this.results.innerHTML = content ? content.outerHTML : '';
      } catch (_) { /* stil falen */ }
    }
  });

  // Productkaart: kristal-swatches wisselen afbeelding en variant
  define('product-card', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const swatch = e.target.closest('.swatch[data-variant-id]');
        if (!swatch) return;
        e.preventDefault();
        this.select(swatch);
      });
    }
    select(swatch) {
      $$('.swatch[data-variant-id]', this).forEach((s) => {
        const active = s === swatch;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      const img = $('.card__img--primary', this);
      if (img && swatch.dataset.variantImage) {
        img.removeAttribute('srcset');
        img.src = swatch.dataset.variantImage;
      }
      const input = $('[data-card-variant]', this);
      if (input) input.value = swatch.dataset.variantId;
      const submit = $('[data-card-submit]', this);
      if (submit) submit.disabled = swatch.dataset.variantAvailable !== 'true';
      const link = $('[data-card-link]', this);
      if (link && swatch.dataset.variantUrl) link.href = swatch.dataset.variantUrl;
      const name = $('[data-card-swatch-name]', this);
      if (name) {
        const intention = swatch.dataset.crystalIntention;
        name.innerHTML = '';
        name.append(document.createTextNode(swatch.dataset.crystalName || ''));
        if (intention) {
          const span = document.createElement('span');
          span.className = 'swatch-label__intention';
          span.textContent = ` · ${intention}`;
          name.append(span);
        }
      }
    }
  });

  // Aantal-stepper op de productpagina
  define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-step]');
        if (!step) return;
        const input = $('.qty__input', this);
        const min = Number(input.min) || 0;
        input.value = Math.max(min, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyStep));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  });

  // Productgalerij
  define('product-gallery', class extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.slides = $$('.gallery-viewer__slide', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.indexEl = $('[data-gallery-index]', this);
      if (!this.track) return;
      this.track.addEventListener('scroll', debounce(() => this.sync(), 60), { passive: true });
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.galleryThumb))));
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.goTo(this.current() - 1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.goTo(this.current() + 1));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.goTo(this.current() + 1);
        if (e.key === 'ArrowLeft') this.goTo(this.current() - 1);
      });
    }
    current() {
      return Math.round(this.track.scrollLeft / this.track.clientWidth);
    }
    goTo(index, instant = false) {
      const n = this.slides.length;
      if (!n) return;
      const i = (index + n) % n;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: instant ? 'instant' : 'smooth' });
      if (instant) this.sync();
    }
    goToMedia(mediaId, instant = false) {
      const i = (this.slides || []).findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i, instant);
    }
    sync() {
      const i = this.current();
      this.thumbs.forEach((t, idx) => t.classList.toggle('is-active', idx === i));
      this.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      if (this.indexEl) this.indexEl.textContent = i + 1;
    }
  });

  // Productpagina: variantkeuze
  define('product-page', class extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.picker = $('variant-picker', this);
      this.form = $('form[data-product-form]', this);
      this.gallery = $('product-gallery', this);
      if (this.picker) this.picker.addEventListener('change', () => this.onChange());
      const input = $('[data-variant-input]', this);
      this.variant = this.product.variants.find((v) => String(v.id) === input?.value) || this.product.variants[0];
      if (this.variant.media_id && this.gallery) this.gallery.goToMedia(this.variant.media_id, true);
      this.updateStock();
      this.markUnavailable();
    }
    selectedOptions() {
      const groups = $$('.variant-picker__group', this.picker);
      return groups.map((g) => $('input:checked', g)?.value);
    }
    onChange() {
      const options = this.selectedOptions();
      const variant = this.product.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.variant = variant;
      this.updateLabels();
      if (!variant) {
        this.setButton(false, strings.unavailable);
        return;
      }
      $('[data-variant-input]', this).value = variant.id;
      this.setButton(variant.available, variant.available ? strings.addToCart : strings.soldOut);
      this.updatePrice();
      this.updateStock();
      this.updateNotify();
      this.updateMeaning();
      this.markUnavailable();
      if (variant.media_id && this.gallery) this.gallery.goToMedia(variant.media_id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
    setButton(enabled, label) {
      const btn = $('[data-add-button]', this);
      if (!btn) return;
      btn.disabled = !enabled;
      const l = $('[data-add-label]', btn);
      if (l) l.textContent = label;
    }
    updatePrice() {
      const v = this.variant;
      const price = $('[data-price]', this);
      if (price) {
        $('.price__current', price).textContent = formatMoney(v.price);
        const compare = $('[data-price-compare]', price);
        const badge = $('[data-price-badge]', price);
        const onSale = v.compare_at_price > v.price;
        price.classList.toggle('price--sale', onSale);
        if (compare) {
          compare.hidden = !onSale;
          compare.textContent = onSale ? formatMoney(v.compare_at_price) : '';
        }
        if (badge) {
          badge.hidden = !onSale;
          if (onSale) badge.textContent = `Bespaar ${Math.round(((v.compare_at_price - v.price) * 100) / v.compare_at_price)}%`;
        }
      }
      const addPrice = $('[data-add-price]', this);
      if (addPrice) addPrice.textContent = formatMoney(v.price);
    }
    updateLabels() {
      $$('.variant-picker__group', this.picker).forEach((g, i) => {
        const checked = $('input:checked', g);
        const label = $(`[data-option-selected="${i}"]`, this.picker);
        if (checked && label) label.textContent = checked.dataset.crystalName || checked.value;
      });
    }
    updateMeaning() {
      const box = $('[data-crystal-meaning]', this);
      const checked = this.picker && $('input:checked', this.picker);
      if (!box || !checked) return;
      const text = checked.dataset.crystalText;
      box.hidden = !text;
      box.style.setProperty('--swatch', checked.style.getPropertyValue('--swatch'));
      const intention = checked.dataset.crystalIntention;
      $('[data-crystal-meaning-title]', box).textContent = checked.dataset.crystalName + (intention ? ` · ${intention}` : '');
      $('[data-crystal-meaning-text]', box).textContent = text || '';
    }
    updateNotify() {
      const box = $('[data-notify]', this);
      if (!box || !this.variant) return;
      box.hidden = this.variant.available;
      const input = $('[data-notify-variant]', this);
      if (input) input.value = this.variant.title;
    }
    updateStock() {
      const note = $('[data-stock-note]', this);
      if (!note || !this.variant) return;
      const qty = this.variant.inventory;
      if (this.variant.available && typeof qty === 'number' && qty > 0 && qty < 10) {
        note.textContent = qty === 1 ? 'Nog maar 1 op voorraad' : `Nog maar ${qty} op voorraad`;
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    }
    markUnavailable() {
      // Bij één optie: markeer uitverkochte waarden
      if (!this.picker || this.product.variants[0].options.length !== 1) return;
      $$('input[type="radio"]', this.picker).forEach((input) => {
        const v = this.product.variants.find((x) => x.options[0] === input.value);
        input.classList.toggle('is-unavailable', !!v && !v.available);
      });
    }
  });

  define('variant-picker', class extends HTMLElement {});

  // Levertijd-afteller (Europe/Amsterdam)
  define('delivery-countdown', class extends HTMLElement {
    connectedCallback() {
      this.textEl = $('[data-countdown-text]', this);
      this.render();
      this.timer = setInterval(() => this.render(), 30000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    nowInAmsterdam() {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Amsterdam', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
      }).formatToParts(new Date());
      const get = (t) => parts.find((p) => p.type === t)?.value;
      const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return { day: days[get('weekday')], hour: Number(get('hour')), minute: Number(get('minute')) };
    }
    render() {
      if (!this.textEl) return;
      const cutoff = Number(theme.cutoffHour) || 17;
      const { day, hour, minute } = this.nowInAmsterdam();
      const isWeekday = day >= 1 && day <= 5;
      if (isWeekday && hour < cutoff) {
        const minsLeft = (cutoff - hour) * 60 - minute;
        const h = Math.floor(minsLeft / 60);
        const m = minsLeft % 60;
        const time = h > 0 ? `${h}u ${m}m` : `${m} min`;
        this.textEl.innerHTML = (strings.countdownToday || '').replace('[time]', time);
      } else {
        const next = day === 5 || day === 6 || day === 0 ? 'maandag' : 'morgen';
        this.textEl.innerHTML = (strings.countdownNext || '').replace('[day]', next);
      }
    }
  });

  // Sticky 'In winkelwagen'-balk
  define('sticky-atc', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.form);
      const target = this.form && $('[data-add-button]', this.form);
      if (!target) return;
      // Scroll-check i.p.v. IntersectionObserver: werkt ook bij grote sprongen (bijv. terug naar boven)
      let ticking = false;
      const update = () => {
        ticking = false;
        const passed = target.getBoundingClientRect().bottom < 0;
        this.classList.toggle('is-visible', passed);
        this.setAttribute('aria-hidden', passed ? 'false' : 'true');
      };
      window.addEventListener('scroll', () => {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }, { passive: true });
      update();
      $('[data-sticky-submit]', this)?.addEventListener('click', () => {
        if (this.form.requestSubmit) this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
      document.addEventListener('variant:change', (e) => {
        const v = e.detail.variant;
        const price = $('[data-sticky-price]', this);
        if (price) price.textContent = formatMoney(v.price);
        const name = $('[data-sticky-variant]', this);
        const checked = $('variant-picker input:checked');
        if (name && checked) name.textContent = checked.dataset.crystalName || v.title;
        const btn = $('[data-sticky-submit]', this);
        if (btn) btn.disabled = !v.available;
      });
    }
  });

  // Bundel: meerdere producten tegelijk toevoegen
  define('bundle-builder', class extends HTMLElement {
    connectedCallback() {
      this.discount = Number(this.dataset.discount) || 0;
      this.addEventListener('change', (e) => {
        const select = e.target.closest('select[data-bundle-variant]');
        if (!select) return;
        const option = select.selectedOptions[0];
        const item = select.closest('[data-bundle-item]');
        const img = $('.bundle__img', item);
        if (img && option.dataset.image) { img.removeAttribute('srcset'); img.src = option.dataset.image; }
        const price = $('[data-bundle-price]', item);
        if (price) price.textContent = formatMoney(option.dataset.price);
        this.updateTotal();
      });
      $('[data-bundle-submit]', this)?.addEventListener('click', (e) => this.submit(e.currentTarget));
    }
    inputs() { return $$('[data-bundle-variant]', this); }
    updateTotal() {
      const total = this.inputs().reduce((sum, el) => {
        const price = el.tagName === 'SELECT' ? el.selectedOptions[0].dataset.price : el.dataset.price;
        return sum + Number(price || 0);
      }, 0);
      const discounted = Math.round(total * (100 - this.discount) / 100);
      const t = $('[data-bundle-total]', this);
      const c = $('[data-bundle-compare]', this);
      if (t) t.textContent = formatMoney(discounted);
      if (c) c.textContent = formatMoney(total);
    }
    async submit(btn) {
      btn.classList.add('is-loading');
      try {
        const items = this.inputs().map((el) => ({ id: Number(el.value), quantity: 1 }));
        await Cart.add({ items });
      } catch (err) {
        toast(err.message);
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  });

  // Kristal-quiz
  define('crystal-quiz', class extends HTMLElement {
    connectedCallback() {
      this.intro = $('[data-quiz-intro]', this);
      this.questions = $$('[data-quiz-question]', this);
      this.results = $$('[data-quiz-result]', this);
      this.answers = [];
      $('[data-quiz-start]', this)?.addEventListener('click', () => this.show(this.questions[0]));
      this.addEventListener('click', (e) => {
        const answer = e.target.closest('[data-quiz-answer]');
        if (answer) {
          const step = this.questions.indexOf(answer.closest('[data-quiz-question]'));
          this.answers[step] = answer.dataset.quizAnswer;
          if (step + 1 < this.questions.length) this.show(this.questions[step + 1]);
          else this.finish();
          return;
        }
        if (e.target.closest('[data-quiz-back]')) {
          const step = this.questions.findIndex((q) => q.classList.contains('is-active'));
          if (step > 0) this.show(this.questions[step - 1]);
          return;
        }
        if (e.target.closest('[data-quiz-restart]')) {
          this.answers = [];
          this.show(this.intro);
        }
      });
    }
    show(step) {
      $$('.quiz__step', this).forEach((s) => s.classList.toggle('is-active', s === step));
      const top = this.getBoundingClientRect().top + window.scrollY - 120;
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      const focusable = $('button, legend, h2', step);
      if (focusable) { focusable.setAttribute('tabindex', '-1'); focusable.focus({ preventScroll: true }); }
    }
    finish() {
      const score = {};
      this.answers.forEach((a, i) => { score[a] = (score[a] || 0) + 1 + (i === 0 ? 0.5 : 0); });
      const winner = Object.keys(score).sort((a, b) => score[b] - score[a])[0];
      const result = this.results.find((r) => r.dataset.quizResult === winner) || this.results[0];
      if (!result) return;
      this.show(result);
      // Selecteer automatisch het bijpassende kristal op de productkaarten
      const name = result.dataset.crystalName;
      // Is dat kristal bij een product uitverkocht, dan verbergen we die kaart.
      $$('product-card', result).forEach((card) => {
        const swatch = $$('.swatch[data-variant-id]', card).find((s) => s.dataset.crystalName === name);
        const item = card.closest('li');
        if (!swatch) { if (item) item.hidden = false; return; }
        const available = swatch.dataset.variantAvailable === 'true';
        if (item) item.hidden = !available;
        if (available && card.select) card.select(swatch);
      });
    }
  });

  // Aanbevelingen laden
  define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.children.length) return;
      const io = new IntersectionObserver(async ([entry], obs) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = $('product-recommendations', doc);
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML;
          else this.remove();
        } catch (_) { this.remove(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  });

  /* ---------- Collectiefilters ---------- */
  const filterForm = $('[data-filter-form]');
  if (filterForm) {
    const submit = debounce(() => {
      const params = new URLSearchParams(new FormData(filterForm));
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      window.location.search = params.toString();
    }, 500);
    filterForm.addEventListener('change', (e) => { if (e.target.matches('[data-filter-input]')) submit(); });
  }

  /* ---------- Subtiele reveal bij scrollen ---------- */
  const revealTargets = $$('.section-head, .product-grid > li, .intention, .crystal, .step, .split__content, .review, .icon-row__item, .bundle');
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    revealTargets.forEach((el) => {
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.setAttribute('data-reveal', '');
        const siblings = Array.from(el.parentElement.children);
        el.style.transitionDelay = `${Math.min(siblings.indexOf(el), 4) * 80}ms`;
        io.observe(el);
      }
    });
  }
})();
